/* eslint-disable max-lines */
import React, { useEffect, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { DialogStep, FCWithFragments } from '@modernloop/shared/components';
import { getLocalTimezone } from '@modernloop/shared/datetime';
import { useFlag } from '@modernloop/shared/feature-flag';
import { getExternalErrorMessage, logError } from '@modernloop/shared/utils';
import { Alert, Typography } from '@mui/material';
import { addDays, startOfDay as startOfDayDateFns } from 'date-fns';
import { uniq } from 'lodash';
import { useSnackbar } from 'notistack';

import {
  CandidateAvailabilityEmailPreview_JobStageSettingsV2Fragment,
  DynamicAudience,
  NotificationPreferenceInput,
  RemoteAttachmentInput,
  TemplateType,
  useCandidateAvailabilityRequestUpsertMutation,
  useCandidateAvailabilityTaskCreateMutation,
  useCandidateDetailsQuery,
} from 'src/generated/mloop-graphql';

import { SmartEmployeeSelectResult } from 'src/entities/Select/SmartEmployeeSelect';

import { useCreateLocationHeader } from 'src/hooks/amplitude/useCreateLocationHeader';
import useAuth from 'src/hooks/useAuth';
import { LoggerEvent, useLogEvent } from 'src/hooks/useLogEvent';

import { FileUploadFlow } from 'src/slices/files';

import { getFilesByUploadFlow } from 'src/store/selectors/files';
import { CandidateAvailabilityOptions } from 'src/store/slices/candidate-availability-options';

import IsoTimestamp, { assertIsoTimestamp } from 'src/types/IsoTimestamp';

import { hasCandidatePortalUrl } from 'src/urls/hasCandidatePortalUrl';
import { hasShortUrl } from 'src/urls/hasShortUrl';

import { Tokens as CandidateAvailablityToken } from 'src/utils/PlaceholderFiller/CandidateAvailabilityPlaceholderFiller';

import useRefetchTaskListQuery from 'src/views-new/ApplicationDetailsTaskView/ApplicationContent/useRefetchTaskList';
import EmailContent, { EmailContentTemplate } from 'src/views-new/EmailContent';
import { TaskCreateInputWrapper, getTaskCreateInput } from 'src/views-new/ScheduleTask/CreateModals/shared/types';
import { NumberOfDays } from 'src/views-new/SelfSchedule/TimeframePicker';

import { TEMPLATE_TOKEN_REGEX } from 'src/constants';
import { useSelector } from 'src/store';

import MissingCandidateAvailabilityTokenAlert from './MissingCandidateAvailabilityTokenAlert';
import { useCandidateAvailabilityEmailFiller } from './useCandidateAvailabilityEmailFiller';

type Props = {
  applicationId: string;
  candidateId: string;
  jobStageId: string;
  options: CandidateAvailabilityOptions;
  taskId?: string;
  jobId?: string;
  taskCreatorId?: string;
  taskInput?: TaskCreateInputWrapper;
  onOptionsChanged: (options: CandidateAvailabilityOptions) => void;
  onSaveSuccess?: () => void;
  onEditOptions?: () => void;
  onClose: (emailSent?: boolean, taskId?: string) => void;
  ccRecipients?: NotificationPreferenceInput | null;
  bccRecipients?: NotificationPreferenceInput | null;
};

type Fragments = {
  jobStageSettingsV2: CandidateAvailabilityEmailPreview_JobStageSettingsV2Fragment;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CREATE_TASK_MUTATION = gql`
  mutation CandidateAvailabilityTaskCreate($input: TaskCreateInput!) {
    taskCreate(input: $input) {
      task {
        id
      }
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CandidateAvailabilityRequestMutation = gql`
  mutation CandidateAvailabilityRequestUpsert($input: CandidateAvailabilityOptionsUpsertInput!) {
    candidateAvailabilityOptionsUpsert(input: $input) {
      options {
        id
      }
    }
  }
`;

const CandidateAvailabilityEmailPreview: FCWithFragments<Fragments, Props> = ({
  applicationId,
  candidateId,
  jobStageId,
  options,
  taskId,
  taskCreatorId,
  jobId,
  taskInput,
  onOptionsChanged,
  onSaveSuccess,
  onEditOptions,
  jobStageSettingsV2,
  onClose,
  ccRecipients,
  bccRecipients,
}): JSX.Element => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { logEvent } = useLogEvent();

  const [selectedTemplate, setSelectedTemplate] = useState(
    jobStageSettingsV2?.availabilityRequestEmailTemplateId?.template
  );
  const [toFreeform, setToFreeform] = useState<string[]>([]);
  const [ccEmployeeIds, setCcEmployeeIds] = useState<string[]>(uniq(ccRecipients?.employeeIds || []));
  const [ccDynamicAudience, setCcDynamicAudience] = useState<DynamicAudience[]>(ccRecipients?.dynamicAudiences || []);
  const [bccDynamicAudience, setBccDynamicAudience] = useState<DynamicAudience[]>(
    bccRecipients?.dynamicAudiences || []
  );
  const [bccEmployeeIds, setBccEmployeeIds] = useState<string[]>(bccRecipients?.employeeIds || []);
  const [ccFreeform, setCcFreeform] = useState<string[]>(ccRecipients?.externalEmailAddresses || []);
  const [bccFreeform, setBccFreeform] = useState<string[]>(bccRecipients?.externalEmailAddresses || []);
  const [selectedToFreeformEmail, setSelectedToFreeformEmail] = useState<string[]>([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [savingOptionsWithoutCandidateCommunication, setSavingOptionsWithoutCandidateCommunication] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [showMissingTokenModal, setShowMissingTokenModal] = useState(false);
  const [isMissingCandidateAvailailityUrlToken, setIsMissingCandidateAvailabilityUrlToken] = useState(false);
  const refetchTaskList = useRefetchTaskListQuery();

  const [subject, setSubject] = useState<string>(selectedTemplate?.subject || '');
  const [bodyHtml, setBodyHtml] = useState<string>(selectedTemplate?.body || '');

  const { data, loading } = useCandidateDetailsQuery({ variables: { id: candidateId } });

  const files = useSelector((state) => getFilesByUploadFlow(state, FileUploadFlow.CANDIDATE_AVAILABILITY));

  const { enqueueSnackbar } = useSnackbar();

  const [createAvailabilityRequest] = useCandidateAvailabilityRequestUpsertMutation();

  const orgBreakImprovementsEnabled = useFlag('org_break_improvements');

  const createLocationHeader = useCreateLocationHeader();
  const [createTask] = useCandidateAvailabilityTaskCreateMutation({
    context: {
      headers: {
        ...createLocationHeader,
      },
    },
  });

  const { fillText: candidateAvailabilityTemplateFiller, loading: placeholderFillerLoading } =
    useCandidateAvailabilityEmailFiller(applicationId, jobStageId, data, taskId, taskInput?.assigneeEmployeeId);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    const additionalEmails = (
      data?.candidate?.additionalEmails ? [...data.candidate.additionalEmails] : []
    ) as string[];
    const preferredEmail = (data?.candidate?.email || data?.candidate?.additionalEmails?.[0]) as string;

    // candidate.email might not exist in the list from the ATS (candidate.additionalEmails)
    // if it does, use the ATS list directly
    // if it doesn't, expand the list with the candidate.email as the first item
    let emailList: string[] = [];
    if (additionalEmails.indexOf(preferredEmail) > -1) {
      emailList = additionalEmails;
    } else {
      if (additionalEmails.length) {
        emailList = additionalEmails;
      }
      if (preferredEmail) {
        emailList.unshift(preferredEmail);
      }
    }
    setToFreeform(emailList);
    setSelectedToFreeformEmail(preferredEmail ? [preferredEmail] : []);
  }, [data]);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Explicit instruction: wait for placeholder queries before syncing filled preview (BUG-1017).
  useEffect(() => {
    if (placeholderFillerLoading || !selectedTemplate) {
      return;
    }

    setSubject(candidateAvailabilityTemplateFiller(selectedTemplate.subject || ''));
    setBodyHtml(candidateAvailabilityTemplateFiller(selectedTemplate.body || ''));
  }, [candidateAvailabilityTemplateFiller, placeholderFillerLoading, selectedTemplate]);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    setIsMissingCandidateAvailabilityUrlToken(
      bodyHtml.indexOf(CandidateAvailablityToken.CANDIDATE_AVAILABILITY_URL) === -1
    );
  }, [bodyHtml]);

  const handleEmailTemplateChange = (template: EmailContentTemplate) => {
    if (!template) {
      return;
    }
    setSelectedTemplate(template);

    if (placeholderFillerLoading) {
      setSubject(template.subject || '');
      setBodyHtml(template.body || '');
      return;
    }

    setSubject(candidateAvailabilityTemplateFiller(template.subject || ''));
    setBodyHtml(candidateAvailabilityTemplateFiller(template.body || ''));
  };

  const handleTo = (freeformEmails: string[]) => {
    setSelectedToFreeformEmail(freeformEmails);
  };

  const handleCc = (result: SmartEmployeeSelectResult) => {
    setCcEmployeeIds(result.selectedEmployeeIds);
    setCcFreeform(result.selectedEmailAddresses);
    setCcDynamicAudience(result.dynamicAudiences);
  };

  const handleBcc = (result: SmartEmployeeSelectResult) => {
    setBccEmployeeIds(result.selectedEmployeeIds);
    setBccFreeform(result.selectedEmailAddresses);
    setBccDynamicAudience(result.dynamicAudiences);
  };

  const handleSendEmail = async (bypassCandidateAvailabilityUrlTokenCheck = false, saveWithoutSending = false) => {
    const bodyHasPortalUrl = hasCandidatePortalUrl(bodyHtml) || hasShortUrl(bodyHtml);
    const skipTokenCheck = bodyHasPortalUrl || bypassCandidateAvailabilityUrlTokenCheck;

    if (!skipTokenCheck && isMissingCandidateAvailailityUrlToken) {
      setShowMissingTokenModal(true);
      return;
    }
    const attachments: RemoteAttachmentInput[] = [];

    if (files) {
      files.forEach((file) => {
        if (!file.path) {
          return;
        }
        attachments.push({ name: file.name, path: file.path });
      });
    }

    if (!saveWithoutSending) {
      if (!data?.candidate?.email) {
        return;
      }
      if (!auth.userInfo?.employeeEmail) {
        return;
      }
      if (!selectedToFreeformEmail.length) {
        return;
      }
    }

    if (saveWithoutSending) {
      setSavingOptionsWithoutCandidateCommunication(true);
    } else {
      setSendingEmail(true);
    }

    const finalOptions = { ...options };

    // Deleting the UI only fields
    delete finalOptions.error;
    delete finalOptions.loading;
    delete finalOptions.isValidInterviewPlan;

    async function submitOptions() {
      try {
        const getInclusionDays = (value: NumberOfDays) => {
          let date = startOfDayDateFns(new Date());
          const inclusionDays: IsoTimestamp[] = [];

          for (let i = 0; i < value; i++) {
            inclusionDays.push(assertIsoTimestamp(date.toISOString()));
            date = addDays(date, 1);
          }

          return inclusionDays;
        };

        const isValidPlan = options.isValidInterviewPlan ?? false;

        let inclusionDays: string[] | undefined;
        // Only set inclusion days if rolling days is off
        // Default to inclusiongDays on options, otherwise 14 days worth
        if (!finalOptions.useRollingDays) {
          inclusionDays = finalOptions.inclusionDays ?? getInclusionDays(finalOptions.timeframeNumberOfDays ?? 14);
        }

        const { data: createdAvailabilityRequest } = await createAvailabilityRequest({
          variables: {
            input: {
              taskId: finalOptions.taskId,
              numberOfDays: finalOptions.numberOfDays,
              minutesPerDays: finalOptions.minutesPerDays,
              minimumTimeBlockMinutes: finalOptions.minimumTimeBlockMinutes,
              advanceNoticeMinutes: finalOptions.advanceNoticeMinutes ?? 24 * 60,
              timezone: getLocalTimezone(),
              candidateNote: finalOptions.candidateNote,
              shouldRespectLoadLimit: isValidPlan && (finalOptions.shouldRespectLoadLimit ?? true),
              // The upsert input treats an absent field as false on create, so this write always
              // sends a value. The job-stage cascade default is the fallback: it keeps a caller
              // that forgets to seed the field from silently turning the setting off.
              bookIfPerfectScheduleFound:
                finalOptions.bookIfPerfectScheduleFound ??
                jobStageSettingsV2?.candidateAvailabilitySettingsV2?.bookIfPerfectScheduleFound?.value ??
                false,
              suggestOverRecruitingKeywords: isValidPlan && (finalOptions.canScheduleOverRecruitingKeywords ?? false),
              suggestOverAvailableKeywords: isValidPlan && (finalOptions.canScheduleOverAvailableKeywords ?? false),
              suggestOverFreeTime: isValidPlan && (finalOptions.canScheduleOverFreeTime ?? false),
              rollingDays: finalOptions.useRollingDays ? finalOptions.timeframeNumberOfDays : 0,
              inclusionDays,
              email: saveWithoutSending
                ? undefined
                : {
                    toEmailAddress: selectedToFreeformEmail[0],
                    ccEmployeeIds,
                    bccEmployeeIds,
                    ccExternalAddresses: ccFreeform || [],
                    bccExternalAddresses: bccFreeform,
                    ccDynamicAudiences: ccDynamicAudience,
                    bccDynamicAudiences: bccDynamicAudience,
                    summary: subject,
                    description: bodyHtml,
                    attachments,
                    emailTemplateId: selectedTemplate?.id,
                  },
            },
          },
        });
        try {
          await refetchTaskList();
        } catch (refetchError) {
          logError(refetchError);
        }
        logEvent(LoggerEvent.CLIENT_CANDIDATE_AVAILABILITY_OPTIONS, { applicationId, jobStageId });
        logEvent(LoggerEvent.CLIENT_CANDIDATE_AVAILABILITY_EMAIL_SENT, { applicationId, jobStageId });

        onOptionsChanged({
          ...options,
          id: createdAvailabilityRequest?.candidateAvailabilityOptionsUpsert?.options?.id,
          taskId: finalOptions.taskId,
          lastCandidateCommunicationSentAt: new Date().toISOString(),
        });

        setSendingEmail(false);
        setSavingOptionsWithoutCandidateCommunication(false);

        if (!saveWithoutSending) {
          enqueueSnackbar(t('hiring-dashboard-candidates:candidates.shareOptions.emailSentToCandidate'), {
            variant: 'success',
          });
        }

        if (onSaveSuccess) {
          onSaveSuccess();
        }
        onClose(!saveWithoutSending, finalOptions.taskId);
      } catch (e) {
        setError(getExternalErrorMessage(e));
        setSendingEmail(false);
        setSavingOptionsWithoutCandidateCommunication(false);
      }
    }

    if (taskId) {
      finalOptions.taskId = taskId;
    }

    if (!taskId && taskInput) {
      const saveInput = getTaskCreateInput(taskInput, orgBreakImprovementsEnabled);

      try {
        const taskResult = await createTask({
          variables: {
            input: saveInput,
          },
        });

        const resultTaskId = taskResult.data?.taskCreate?.task?.id;
        finalOptions.taskId = resultTaskId;
        await submitOptions();
      } catch (e) {
        enqueueSnackbar(`${e.message}`, {
          variant: 'error',
        });
        logError(e);

        // Enable the Save without sending & Save & send email button after task creation fails
        if (saveWithoutSending) {
          setSavingOptionsWithoutCandidateCommunication(false);
        } else {
          setSendingEmail(false);
        }
      }
    } else {
      await submitOptions();
    }
  };

  const bodyUnusedTokens = bodyHtml.match(TEMPLATE_TOKEN_REGEX) || [];
  const subjectUnusedTokens = subject.match(TEMPLATE_TOKEN_REGEX) || [];
  const unusedTokens =
    uniq([...bodyUnusedTokens, ...subjectUnusedTokens]).filter(
      (value) => value !== `{{${CandidateAvailablityToken.CANDIDATE_AVAILABILITY_URL}}}`
    ) || [];
  const hasUnusedToken = !!unusedTokens.length;

  const getSendEmailDisabledTooltip = () => {
    if (hasUnusedToken) {
      return t('hiring-dashboard-candidates:candidates.shareOptions.unusedTokensTooltip', {
        tokens: unusedTokens.join(', '),
      });
    }

    if (!data?.candidate?.email) {
      return t('hiring-dashboard-candidates:candidates.shareOptions.candidateEmailNotPresent');
    }

    return undefined;
  };

  const isSendEmailDisabled =
    loading ||
    placeholderFillerLoading ||
    savingOptionsWithoutCandidateCommunication ||
    sendingEmail ||
    hasUnusedToken ||
    !selectedToFreeformEmail.length ||
    !subject;

  return (
    <>
      <DialogStep
        title={t('hiring-dashboard-candidates:candidates.shareOptions.emailRequestTitle')}
        subTitle={
          taskId || !taskInput
            ? t('hiring-dashboard-candidates:candidates.shareOptions.stepWithTask')
            : t('hiring-dashboard-candidates:candidates.shareOptions.stepWithoutTask')
        }
        submitOptions={{
          dataTestId: 'candidate-availability-share-submit-button',
          label: onEditOptions
            ? t('hiring-dashboard-candidates:candidates.shareOptions.saveAndSendEmail')
            : t('hiring-dashboard-candidates:candidates.shareOptions.sendEmail'),
          isDisabled: isSendEmailDisabled,
          isLoading: sendingEmail,
          tooltip: getSendEmailDisabledTooltip(),
          onClick: () => handleSendEmail(),
        }}
        cancelOptions={{
          label: t('hiring-dashboard-common:common.cancel'),
          onClick: () => onClose(),
        }}
        secondaryOptions={
          onEditOptions
            ? {
                label: t('hiring-dashboard-candidates:candidates.shareOptions.saveWithoutSending'),
                isDisabled: !toFreeform.length || savingOptionsWithoutCandidateCommunication || sendingEmail,
                isLoading: savingOptionsWithoutCandidateCommunication,
                onClick: () => {
                  handleSendEmail(true /* bypassCandidateAvailabilityUrlTokenCheck */, true /* saveWithoutSending */);
                },
              }
            : undefined
        }
        onClose={() => onClose()}
      >
        <EmailContent
          applicationId={applicationId}
          alerts={
            <Alert severity="info">
              <Typography variant="caption" fontWeight={400}>
                {t('hiring-dashboard-candidates:candidates.shareOptions.tokenInfoAlert', {
                  token: '{{CANDIDATE_AVAILABILITY_URL}}',
                })}
              </Typography>
            </Alert>
          }
          filterBccDynamicAudiences={[
            DynamicAudience.DebriefAttendees,
            DynamicAudience.InterviewersOnSchedule,
            DynamicAudience.CandidatesPreviousInterviewers,
          ]}
          filterCcDynamicAudiences={[
            DynamicAudience.DebriefAttendees,
            DynamicAudience.InterviewersOnSchedule,
            DynamicAudience.CandidatesPreviousInterviewers,
          ]}
          sendError={error}
          toFreeformEmails={toFreeform}
          ccEmployeeIds={ccEmployeeIds}
          defaultCcEmployeeIds={ccEmployeeIds}
          defaultCcFreeformEmails={ccFreeform}
          ccFreeformEmails={ccFreeform}
          ccDynamicAudiences={ccDynamicAudience}
          defaultBccEmployeeIds={bccEmployeeIds}
          bccFreeformEmails={bccFreeform}
          defaultBccFreeformEmails={bccFreeform}
          bccEmployeeIds={bccEmployeeIds}
          bccDynamicAudiences={bccDynamicAudience}
          loading={loading || placeholderFillerLoading}
          subject={subject}
          bodyHtml={bodyHtml}
          fileUploadFlow={FileUploadFlow.CANDIDATE_AVAILABILITY}
          templateType={TemplateType.CandidateAvailabilityRequest}
          selectedTemplateId={selectedTemplate?.id}
          selectedToFreeformEmail={selectedToFreeformEmail.length ? selectedToFreeformEmail[0] : undefined}
          assigneeEmployeeId={taskInput?.assigneeEmployeeId}
          taskId={taskId}
          jobId={jobId}
          taskCreatorId={taskCreatorId}
          subscriberEmployeeIds={taskInput?.subscriberEmployeeIds ?? []}
          onChangeSubject={setSubject}
          onChangeBodyHtml={setBodyHtml}
          onChangeTemplate={handleEmailTemplateChange}
          onToChanged={handleTo}
          onCcChanged={handleCc}
          onBccChanged={handleBcc}
        />
      </DialogStep>
      {showMissingTokenModal && (
        <MissingCandidateAvailabilityTokenAlert
          goBack={() => setShowMissingTokenModal(false)}
          onAppendAndContinue={() => {
            setShowMissingTokenModal(false);
            handleSendEmail(true /* bypassCandidateAvailabilityUrlTokenCheck */);
          }}
        />
      )}
    </>
  );
};

CandidateAvailabilityEmailPreview.fragments = {
  jobStageSettingsV2: gql`
    fragment CandidateAvailabilityEmailPreview_jobStageSettingsV2 on JobStageSettingsV2 {
      id
      availabilityRequestEmailTemplateId {
        templateId
        template {
          id
          ...TemplateSelectRow_template
        }
      }
      candidateAvailabilitySettingsV2 {
        bookIfPerfectScheduleFound {
          value
        }
      }
    }
  `,
};

export default CandidateAvailabilityEmailPreview;
